// Breaker Panel (G3/G4): the Go-Live Panel's calls and the small rules the lists share.
// The database decides everything (migrations 262/264/265); this file only asks and shapes answers for the screens.

export const PANEL_TABS = [
    { key: 'panel', label: 'Panel' },
    { key: 'test-list', label: 'Test list' },
    { key: 'outbox', label: 'Test Outbox' },
    { key: 'history', label: 'History' },
];

export const TEST_CONTACT_KINDS = [
    { key: 'phone', label: 'Phone', placeholder: '(404) 555-0100' },
    { key: 'email', label: 'Email', placeholder: 'tester@company.com' },
    { key: 'client_account', label: 'Client account', placeholder: 'Search by client name' },
    { key: 'driver', label: 'Driver', placeholder: 'Driver id' },
    { key: 'staff', label: 'Staff', placeholder: 'Staff user id' },
];

export const AI_CEILING_LABEL = { acts: 'AI may act', drafts: 'AI drafts only', reads: 'AI reads only', none: 'No AI' };

export const DOOR_LABEL = {
    D1: 'Texts & calls', D2: 'Email', D3: 'Push', D4: 'Money', D5: 'Driver jobs', D6: 'Client pages & links', D7: 'Invoices & accounting', D8: 'Paid services',
};

// Which module a screen belongs to (for the thin yellow "in Test" banner). Longest prefix wins; null = no banner.
const ROUTE_MODULES = [
    ['/communications', 'comms'],
    ['/clients', 'crm'],
    ['/dispatch', 'dispatch'], ['/engagements', 'dispatch'], ['/driver', 'dispatch'],
    ['/schedule-calendar', 'schedule'],
    ['/financials/invoicing', 'invoices'], ['/financials/auto-billing', 'invoices'], ['/income', 'invoices'],
    ['/financials/payments', 'payments'], ['/financials/scheduled-payments', 'payments'],
    ['/events', 'events'],
];

export function moduleForPath(pathname = '') {
    const path = String(pathname || '').toLowerCase();
    let best = null;
    for (const [prefix, module] of ROUTE_MODULES) {
        if ((path === prefix || path.startsWith(`${prefix}/`)) && (!best || prefix.length > best[0].length)) best = [prefix, module];
    }
    return best ? best[1] : null;
}

/** Show TEST records? Default: on while the module is in Test, off once Live. The person's own toggle wins. */
export function showTestRecords(mode, toggle = null) {
    if (toggle === true || toggle === false) return toggle;
    return mode !== 'live';
}

/** TEST- numbering for confirmations and invoices (display: the stored number never changes, so links between records hold). */
export function displayNumber(number, isTest) {
    if (number === null || number === undefined || number === '') return number;
    const text = String(number);
    return isTest && !text.toUpperCase().startsWith('TEST-') ? `TEST-${text}` : text;
}

/** Keep or drop TEST rows for a list or report. */
export function filterTestRows(rows, { mode, toggle = null, flag = 'is_test' } = {}) {
    if (!Array.isArray(rows)) return rows;
    return showTestRecords(mode, toggle) ? rows : rows.filter((row) => !row?.[flag]);
}

export function companyNameMatches(typed, names = []) {
    const value = String(typed || '').trim().toLowerCase();
    return Boolean(value) && names.some((name) => String(name || '').trim().toLowerCase() === value);
}

export function friendlyBreakerError(error) {
    const message = String(error?.message || error || '');
    if (/turn AI On/i.test(message)) return 'Only the company Owner can turn AI On. Off or Test are fine.';
    if (/fresh code|authenticator/i.test(message)) return 'Enter the 6-digit code from your authenticator app.';
    if (/company name/i.test(message)) return 'Type the company name exactly as shown.';
    if (/walkthrough|override/i.test(message)) return 'Pick a Passed walkthrough, or write why you are going Live without one.';
    if (/inside only/i.test(message)) return 'This module is inside only: nothing leaves the app, so it has no Live/Test switch.';
    return message || 'That did not work. Please try again.';
}

// ---- calls ------------------------------------------------------------------------------------------------------------

async function rpc(client, name, args) {
    const { data, error } = await client.rpc(name, args);
    if (error) throw error;
    return data;
}

export const loadPanel = (client, orgId = null) => rpc(client, 'breaker_panel_get', { p_org: orgId });
export const loadPreflight = (client, orgId, moduleKey) => rpc(client, 'breaker_preflight', { p_org: orgId, p_module: moduleKey });
export const goLive = (client, { orgId, moduleKey, companyName, walkthroughRef, overrideReason }) => rpc(client, 'breaker_go_live', {
    p_org: orgId, p_module: moduleKey, p_confirm_company_name: companyName,
    p_walkthrough_ref: walkthroughRef || null, p_override_reason: overrideReason || null,
});
export const goTest = (client, { orgId, moduleKey, reason }) => rpc(client, 'breaker_go_test', { p_org: orgId, p_module: moduleKey, p_reason: reason });
export const everythingToTest = (client, { orgId, reason }) => rpc(client, 'breaker_everything_to_test', { p_org: orgId, p_reason: reason });
export const deleteTestRecords = (client, { orgId, moduleKey, companyName }) => rpc(client, 'breaker_delete_test_records', {
    p_org: orgId, p_module: moduleKey, p_confirm_company_name: companyName,
});
export const setAi = (client, { orgId, moduleKey, aiMode, reason = null }) => rpc(client, 'breaker_set_ai', {
    p_org: orgId, p_module: moduleKey, p_ai_mode: aiMode, p_reason: reason,
});
// Pause all AI / Resume (the existing emergency switch, migration 216) — kept as its own button on the Comms tile.
export const pauseAllAi = (client, orgId) => rpc(client, 'ai_pause_all', { p_org: orgId });
export const resumeAllAi = (client, orgId) => rpc(client, 'ai_resume_all', { p_org: orgId });
export const AI_CHOICES = [{ key: 'off', label: 'Off' }, { key: 'test', label: 'Test' }, { key: 'on', label: 'On' }];
export const AI_CEILING_HELP = {
    acts: 'May answer and act on its own (within the AI settings).',
    drafts: 'Only prepares drafts — a person sends, assigns, merges or invoices.',
    reads: 'Only reads and summarizes.',
};

export const releaseOutbox = (client, id) => rpc(client, 'breaker_outbox_release', { p_id: id });
export const discardOutbox = (client, id, note = null) => rpc(client, 'breaker_outbox_discard', { p_id: id, p_note: note });

export async function loadTestList(client, orgId) {
    const { data, error } = await client.from('org_test_contacts').select('id, kind, value, label, ai_tester, created_at').eq('org_id', orgId).order('created_at');
    if (error) throw error;
    return data || [];
}
export async function addTestContact(client, { orgId, kind, value, label }) {
    const { error } = await client.from('org_test_contacts').insert({ org_id: orgId, kind, value, label: label || null });
    if (error) throw error;
}
/** Test list: find this company's client accounts by name, so staff pick a name instead of pasting an id. */
export async function searchClientAccounts(client, orgId, query) {
    const term = String(query || '').trim().replace(/[%_\\,()]/g, ' ').replace(/\s+/g, ' ').trim();
    if (term.length < 2) return [];
    const { data, error } = await client.from('accounts').select('id, account_name').eq('org_id', orgId)
        .ilike('account_name', `%${term}%`).order('account_name').limit(8);
    if (error) throw error;
    return (data || []).filter((row) => row?.id && row.account_name);
}
/** Names for client-account rows already on the test list (rows store the account id). */
export async function loadClientAccountNames(client, orgId, ids) {
    const unique = [...new Set((ids || []).filter(Boolean))];
    if (!unique.length) return {};
    const { data, error } = await client.from('accounts').select('id, account_name').eq('org_id', orgId).in('id', unique);
    if (error) throw error;
    return Object.fromEntries((data || []).map((row) => [row.id, row.account_name]));
}
export const looksLikeId = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || '').trim());
/** Phones and emails only: mark (or unmark) as an AI Attendant tester; the database keeps the AI list in step. */
export async function setTestContactAiTester(client, id, aiTester) {
    const { error } = await client.from('org_test_contacts').update({ ai_tester: Boolean(aiTester) }).eq('id', id);
    if (error) throw error;
}
export async function removeTestContact(client, id) {
    const { error } = await client.from('org_test_contacts').delete().eq('id', id);
    if (error) throw error;
}
export async function loadOutbox(client, orgId) {
    const { data, error } = await client.from('test_outbox')
        .select('id, module_key, door, channel, reason, would_have_gone_to, summary, source, status, created_at, resolved_at')
        .eq('org_id', orgId).order('created_at', { ascending: false }).limit(200);
    if (error) throw error;
    return data || [];
}
export async function loadHistory(client, orgId) {
    const { data, error } = await client.from('module_mode_history')
        .select('id, module_key, from_mode, to_mode, from_ai_mode, to_ai_mode, changed_at, reason, walkthrough_ref, override, source')
        .eq('org_id', orgId).order('changed_at', { ascending: false }).limit(200);
    if (error) throw error;
    return data || [];
}

/**
 * The breaker state for the signed-in person's ACTIVE company: { orgId, modes: { [module_key]: { mode, ai_mode } } }.
 * Filtered by the active company id (auth_org_id), so a person in several companies never sees another company's breakers
 * even though RLS would let them read their other memberships' rows.
 */
export async function loadModuleModes(client) {
    const { data: orgId, error: orgError } = await client.rpc('auth_org_id');
    if (orgError) throw orgError;
    if (!orgId) return { orgId: null, modes: {} };
    const { data, error } = await client.from('org_module_modes').select('module_key, mode, ai_mode').eq('org_id', orgId);
    if (error) throw error;
    return { orgId, modes: Object.fromEntries((data || []).map((row) => [row.module_key, { mode: row.mode, ai_mode: row.ai_mode }])) };
}

/** The signed-in person's "Include TEST" choice for dashboards and reports (their active company). */
export const loadReportIncludeTest = async (client) => Boolean(await rpc(client, 'breaker_report_include_test_get', { p_org: null }));
export const setReportIncludeTest = (client, include) => rpc(client, 'breaker_set_report_include_test', { p_include: Boolean(include) });

/** Record a walkthrough result (Owner / Tenant Admin; the database checks). */
export async function recordWalkthrough(client, { orgId, moduleKey, status, notes }) {
    const { data, error } = await client.from('org_module_walkthroughs')
        .insert({ org_id: orgId, module_key: moduleKey, status, notes: notes || null }).select('id').single();
    if (error) throw error;
    return data;
}

export const WALKTHROUGH_STATUS_LABEL = { passed: 'Passed', failed: 'Failed', in_progress: 'In progress' };

/** One-tap reasons for switching back to Test (fix round 1, finding 16). The first is the default. */
export const TEST_REASONS = ['Emergency', 'Something looks wrong', 'Fixing a setting', 'Planned maintenance', 'More testing'];

/** "Emergency" or "Emergency — note" (the note is optional; nothing needs typing). */
export function testReasonText(pick, note = '') {
    const base = TEST_REASONS.includes(pick) ? pick : TEST_REASONS[0];
    const extra = String(note || '').trim();
    return extra ? `${base} — ${extra}` : base;
}

/**
 * A fresh authenticator step (go-live and delete need a TOTP code from the last 10 minutes): verify the typed code on the
 * person's verified authenticator. The session then carries a new code time, which the database checks.
 */
export async function freshAuthenticatorStep(auth, code) {
    const digits = String(code || '').replace(/\D/g, '');
    if (digits.length !== 6) throw new Error('Enter the 6-digit code from your authenticator app.');
    const { data: factors, error: listError } = await auth.mfa.listFactors();
    if (listError) throw listError;
    const factor = (factors?.totp || []).find((f) => f.status === 'verified');
    if (!factor) throw new Error('Set up an authenticator app on your account first.');
    const { error } = await auth.mfa.challengeAndVerify({ factorId: factor.id, code: digits });
    if (error) throw new Error('That code did not match. Try the newest code in your authenticator app.');
}

/** "Keys present" (existence only) from the server: Supabase side (Edge Function) and Vercel side (api route). */
export async function loadKeyPresence(client, orgId, { fetchImpl = typeof fetch === 'function' ? fetch : null } = {}) {
    const result = {};
    try {
        const { data } = await client.functions.invoke('breaker-keys', { body: {} });
        Object.assign(result, data?.keys || {});
    } catch { /* shown as "not checked" */ }
    try {
        const { data: session } = await client.auth.getSession();
        const token = session?.session?.access_token;
        if (fetchImpl && token && orgId) {
            const response = await fetchImpl(`/api/breaker/keys?orgId=${encodeURIComponent(orgId)}`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.ok) {
                const body = await response.json();
                Object.entries(body?.keys || {}).forEach(([key, value]) => {
                    result[key] = result[key] ? { ...value, ...result[key], present: Boolean(result[key].present && value.present) } : value;
                });
            }
        }
    } catch { /* shown as "not checked" */ }
    return result;
}
